/**
 * WC26 League Mode — Phase B1 player UI.
 *
 * Minimum: Home tab with the full match-flow state machine. Other tabs
 * (My League / Today / Schedule / History) are present but lightweight —
 * full visual treatment ships in Phase B2.
 *
 * State machine (per spec):
 *   idle → rolling → matched → scheduled → playing → submit
 *        → submitted → confirmed
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useT } from "../../context/LanguageContext";
import { api, API, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  ChevronLeft, Loader2, Trophy, Sparkles, Calendar, ListOrdered,
  Smartphone, Monitor, Flag, Check, Lock, Shuffle, Crown, Eye, Upload,
} from "lucide-react";
import { useWcAccess } from "../../pages/Wc2026Hub";

const TAB_DEFS = [
  { id: "home",     i18n: "wc26.league.tabs.home",     icon: Sparkles },
  { id: "league",   i18n: "wc26.league.tabs.myleague", icon: ListOrdered },
  { id: "today",    i18n: "wc26.league.tabs.today",    icon: Calendar },
  { id: "schedule", i18n: "wc26.league.tabs.schedule", icon: Flag },
  { id: "history",  i18n: "wc26.league.tabs.history",  icon: Trophy },
];

// 48 nations → flag emoji map (best-effort; ASCII fallback for the rest)
const FLAG = {
  "Mexico": "🇲🇽", "South Africa": "🇿🇦", "Korea Republic": "🇰🇷", "Czechia": "🇨🇿",
  "Canada": "🇨🇦", "Bosnia & Herzegovina": "🇧🇦", "Qatar": "🇶🇦", "Switzerland": "🇨🇭",
  "Brazil": "🇧🇷", "Morocco": "🇲🇦", "Haiti": "🇭🇹", "Scotland": "🏴󠁧󠁢󠁳󠁣󠁴󠁿",
  "USA": "🇺🇸", "Paraguay": "🇵🇾", "Australia": "🇦🇺", "Türkiye": "🇹🇷",
  "Germany": "🇩🇪", "Curaçao": "🇨🇼", "Côte d'Ivoire": "🇨🇮", "Ecuador": "🇪🇨",
  "Netherlands": "🇳🇱", "Japan": "🇯🇵", "Sweden": "🇸🇪", "Tunisia": "🇹🇳",
  "Belgium": "🇧🇪", "Egypt": "🇪🇬", "Iran": "🇮🇷", "New Zealand": "🇳🇿",
  "Spain": "🇪🇸", "Cabo Verde": "🇨🇻", "Saudi Arabia": "🇸🇦", "Uruguay": "🇺🇾",
  "France": "🇫🇷", "Senegal": "🇸🇳", "Iraq": "🇮🇶", "Norway": "🇳🇴",
  "Argentina": "🇦🇷", "Algeria": "🇩🇿", "Austria": "🇦🇹", "Jordan": "🇯🇴",
  "Portugal": "🇵🇹", "Congo DR": "🇨🇩", "Uzbekistan": "🇺🇿", "Colombia": "🇨🇴",
  "England": "🏴󠁧󠁢󠁥󠁮󠁧󠁿", "Croatia": "🇭🇷", "Ghana": "🇬🇭", "Panama": "🇵🇦",
};
const flag = (t) => FLAG[t] || "🏳️";

export default function WC26LeagueV2() {
  const { pool: rawPool } = useParams();
  const pool = (rawPool === "console" ? "console" : "mobile");
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { user } = useAuth();
  const access = useWcAccess();
  const [tab, setTab] = useState(() => {
    const t = search.get("tab");
    return ["home", "league", "today", "schedule", "history"].includes(t) ? t : "home";
  });
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/wc26/state?pool=${pool}`);
      setState(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [pool]);
  useEffect(() => { load(); }, [load]);

  const { t } = useT();

  if (!access.isStaff && !access.publicOpen) {
    return (
      <div className="min-h-screen bg-[#020617] text-white pt-10 px-6" data-testid="wc26-league-locked">
        <p className="text-center"><Lock className="w-6 h-6 mx-auto mb-2 text-[#FFD700]" />{t("wc26.league.locked")}</p>
      </div>
    );
  }
  if (!state) {
    return <div className="min-h-screen bg-[#020617] text-white pt-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#FFD700]" /></div>;
  }

  const register = async () => {
    setBusy(true);
    try {
      await api.post(`/wc26/register`, { pool });
      toast.success(t("wc26.league.toastJoined"));
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const ACCENT = "#38BDF8"; // Immersion identity (matches the mode-entry page)

  // Derive the next-cut info from the CURRENT stage (advance_pct lives on the
  // stage, not the player doc — see _h_state response shape).
  const curStage = state.tournament?.stages?.find(
    (s) => state.today_day && s.start_day <= state.today_day && state.today_day <= s.end_day
  );
  const nextCutPct = curStage?.advance_pct ?? null;
  const daysToCut = (curStage && state.today_day != null) ? Math.max(0, curStage.end_day - state.today_day + 1) : null;

  return (
    <div className="min-h-screen bg-[#020617] text-white pb-20" data-testid={`wc26-league-${pool}`}>
      {/* Header — centered emblem */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-5 pb-4">
        {/* back (left) */}
        <div className="mb-2">
          <Link to="/v2/wc2026/immersion" data-testid="wc26-league-back" className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-white/55 hover:text-white">
            <ChevronLeft className="w-3.5 h-3.5" /> {t("wc26.league.backChip")}
          </Link>
        </div>

        {/* centered emblem + title + status·platform */}
        <div className="relative flex flex-col items-center text-center pt-1 pb-2">
          <div aria-hidden className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-40 rounded-full blur-[90px] pointer-events-none" style={{ background: `${ACCENT}22` }} />
          <div className="relative mb-2.5">
            <div aria-hidden className="absolute inset-0 -m-4 rounded-full blur-2xl" style={{ background: "#FFD70033" }} />
            <img src="/wc/wc2026-dark.png" alt="FIFA World Cup 2026" className="relative h-16 sm:h-20 w-auto object-contain drop-shadow-[0_4px_16px_rgba(0,0,0,0.6)]" draggable={false} />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl uppercase tracking-wider leading-none bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(90deg, #ffffff 0%, ${ACCENT} 140%)` }}>
            {t("wc26.league.title")}
          </h1>
          <div className="text-[10px] font-mono uppercase tracking-[0.35em] text-white/45 mt-2">
            {t("wc26.mode.immersion.chapter").replace(/^\d+\s*·\s*/, "")} · FIFA World Cup 2026
          </div>
          <div className="mt-3 flex items-center justify-center gap-2">
            <StatusBadge status={state.tournament.status} />
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10">
              {pool === "mobile" ? <Smartphone className="w-3 h-3" style={{ color: ACCENT }} /> : <Monitor className="w-3 h-3" style={{ color: ACCENT }} />}
              <span className="text-[9px] font-mono uppercase tracking-widest text-white/75">{pool}</span>
            </span>
          </div>
        </div>

        <PlayerRankCard me={state.me} registered={state.registered} accent={ACCENT} nextCutPct={nextCutPct} daysToCut={daysToCut} />

        {/* Iter 98 — direct Play CTA right under the rank card, so a registered
            player jumps straight into today's match without scrolling down to
            the roll card. Only shown when there's a matchday today. */}
        {state.registered && state.today_day && (
          <PlayNowButton
            pool={pool}
            accent={ACCENT}
            hasMatch={!!((state.today_legs && state.today_legs.length) || state.today_match)}
            onPlayed={load}
            onGoHome={() => setTab("home")}
          />
        )}
      </div>

      {/* Sticky sub-nav — tab strip + Rules quick-jump (spec §4.11) */}
      <div className="sticky top-0 z-30 bg-[#020617]/95 backdrop-blur border-b border-white/10">
        <div className="max-w-3xl mx-auto px-2 overflow-x-auto no-scrollbar">
          <div className="flex gap-1 py-2 items-center">
            {TAB_DEFS.map((tb) => {
              const Icon = tb.icon;
              const label = (tb.id === "league" && state.registered && state.me?.rank)
                ? `${t(tb.i18n)} #${state.me.rank}`
                : t(tb.i18n);
              return (
                <button
                  key={tb.id}
                  type="button"
                  onClick={() => setTab(tb.id)}
                  data-testid={`wc26-league-tab-${tb.id}`}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-widest transition-colors ${
                    tab === tb.id
                      ? "bg-[#FFD700] text-[#020617]"
                      : "bg-white/5 text-white/70 hover:text-white"
                  }`}
                >
                  <Icon className="w-3 h-3" /> {label}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => setRulesOpen(true)}
              data-testid="wc26-league-rules-btn"
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-widest bg-white/5 text-white/70 hover:text-white border border-[#38BDF8]/30 ml-auto"
            >
              <Lock className="w-3 h-3" style={{ color: ACCENT }} /> {t("wc26.entry.format")}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 mt-4 space-y-4">
        {!state.registered ? (
          <RegisterCard busy={busy} onRegister={register} pool={pool} />
        ) : (
          <>
            {tab === "home"     && <HomeTab pool={pool} state={state} onChanged={load} userId={user?.id} onJumpTab={setTab} nextCutPct={nextCutPct} daysToCut={daysToCut} accent={ACCENT} />}
            {tab === "league"   && <MyLeagueTab pool={pool} userId={user?.id} advancePct={nextCutPct} />}
            {tab === "today"    && <TodayTab pool={pool} userId={user?.id} />}
            {tab === "schedule" && <ScheduleTab pool={pool} stages={state.tournament?.stages} />}
            {tab === "history"  && <HistoryTab pool={pool} userId={user?.id} />}
          </>
        )}
      </div>

      {rulesOpen && <RulesSheet onClose={() => setRulesOpen(false)} accent={ACCENT} />}
    </div>
  );
}

/* ============================================================ */
/* Iter 98 — Top-level "Play" CTA. Lets a registered player roll */
/* into today's match straight from the top of the page instead  */
/* of scrolling down to the roll card. If they're the odd one out */
/* it shows a "waiting for an opponent" state (queue model — no   */
/* more automatic bye + free points). Click again to re-check.    */
/* ============================================================ */
function PlayNowButton({ pool, accent = "#38BDF8", hasMatch, onPlayed, onGoHome }) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);

  // Auto-poll while waiting in the queue: the incremental draw pairs this
  // player the moment another un-drawn player is available, so re-roll on a
  // light interval and surface the match automatically (no manual re-tap).
  useEffect(() => {
    if (!waiting) return undefined;
    let stopped = false;
    const id = setInterval(async () => {
      try {
        const { data } = await api.post(`/wc26/today/roll`, { pool });
        if (!stopped && data?.match && data?.status !== "waiting") {
          setWaiting(false);
          toast.success("Opponent found — your match is ready!");
          onGoHome?.();
          await onPlayed?.();
        }
      } catch { /* keep waiting silently */ }
    }, 6000);
    return () => { stopped = true; clearInterval(id); };
  }, [waiting, pool]); // eslint-disable-line react-hooks/exhaustive-deps

  const onClick = async () => {
    // Already have a match → just take them to the match surface.
    if (hasMatch) {
      onGoHome?.();
      try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* noop */ }
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/wc26/today/roll`, { pool });
      if (!data?.match || data?.status === "waiting") {
        setWaiting(true);
        toast.message(data?.message || "You're in the queue — waiting for an opponent to join.");
      } else {
        setWaiting(false);
        toast.success("Your match is ready!");
      }
      onGoHome?.();
      await onPlayed?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const label = hasMatch
    ? "Go to today's match"
    : waiting
      ? "Waiting for an opponent…"
      : "Play today's match";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid="wc26-league-play-now"
      className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-[#FFD700] text-[#020617] font-extrabold px-5 py-3 text-sm uppercase tracking-wider disabled:opacity-60"
    >
      {(busy || waiting)
        ? <Loader2 className="w-4 h-4 animate-spin" />
        : <Shuffle className="w-4 h-4" />}
      {label}
    </button>
  );
}

/* ============================================================ */
/* Live status badge (pulsing dot)                              */
/* ============================================================ */
function StatusBadge({ status }) {
  const { t } = useT();
  const MAP = {
    live:         { color: "#22C55E", pulse: true },
    registration: { color: "#FBBF24", pulse: true },
    scheduled:    { color: "#38BDF8", pulse: false },
    complete:     { color: "#71717A", pulse: false },
  };
  const s = MAP[status] || MAP.scheduled;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-extrabold uppercase tracking-[0.3em]"
      style={{ background: `${s.color}1f`, border: `1px solid ${s.color}55`, color: s.color }}
      data-testid="wc26-league-status"
    >
      <span className="relative flex w-1.5 h-1.5">
        {s.pulse && <span className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping" style={{ background: s.color }} />}
        <span className="relative inline-flex rounded-full w-1.5 h-1.5" style={{ background: s.color }} />
      </span>
      {t(`wc26.league.tstatus.${status}`)}
    </span>
  );
}

/* ============================================================ */
/* Player rank mini-card                                        */
/* ============================================================ */
function PlayerRankCard({ me, registered, accent = "#38BDF8", nextCutPct = null, daysToCut = null }) {
  const { t } = useT();
  if (!registered || !me) return null;
  const gd = me.goal_diff ?? me.gd ?? null;
  return (
    <div className="mt-3 space-y-2" data-testid="wc26-league-rank-card">
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-xl bg-black/40 border border-white/10 p-2.5 text-center">
          <div className="text-[9px] font-mono uppercase tracking-widest text-white/50">{t("wc26.league.rank")}</div>
          <div className="font-extrabold text-xl tabular-nums">#{me.rank ?? "—"}</div>
        </div>
        <div className="rounded-xl p-2.5 text-center" style={{ background: "#FFD70015", border: "1px solid #FFD70040" }}>
          <div className="text-[9px] font-mono uppercase tracking-widest text-[#FFD700]">{t("wc26.league.points")}</div>
          <div className="font-extrabold text-xl tabular-nums text-[#FFD700]">{me.points ?? 0}</div>
        </div>
        <div className="rounded-xl p-2.5 text-center" style={{ background: `${accent}12`, border: `1px solid ${accent}33` }}>
          <div className="text-[9px] font-mono uppercase tracking-widest" style={{ color: accent }}>GD</div>
          <div className="font-extrabold text-xl tabular-nums" style={{ color: accent }}>{gd === null ? "—" : `${gd >= 0 ? "+" : ""}${gd}`}</div>
        </div>
      </div>
      {nextCutPct != null && (
        <div className="rounded-xl bg-white/5 border border-white/10 px-3 py-2 flex items-center justify-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-white/60" data-testid="wc26-league-next-cut">
          <Flag className="w-3 h-3" style={{ color: accent }} />
          {t("wc26.league.nextCut")} · {t("wc26.league.cutBar", { pct: nextCutPct })}
          {daysToCut != null && <span className="text-white/40"> · {daysToCut}d</span>}
        </div>
      )}
    </div>
  );
}

/* ============================================================ */
/* Register card (pre-onboard)                                  */
/* ============================================================ */
function RegisterCard({ busy, onRegister, pool }) {
  const { t } = useT();
  const ACCENT = "#38BDF8";
  const stats = [
    { n: "33", l: t("wc26.immersion.stat.1") },
    { n: "1,000", l: t("wc26.immersion.stat.2") },
    { n: "30%", l: t("wc26.immersion.stat.3") },
  ];
  return (
    <div
      className="relative rounded-3xl border overflow-hidden text-center"
      style={{ borderColor: `${ACCENT}30` }}
      data-testid="wc26-league-register-card"
    >
      <div aria-hidden className="absolute inset-0" style={{ background: `radial-gradient(120% 90% at 50% -10%, ${ACCENT}1f 0%, #020617 60%)` }} />
      <div aria-hidden className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: `linear-gradient(${ACCENT} 1px, transparent 1px), linear-gradient(90deg, ${ACCENT} 1px, transparent 1px)`, backgroundSize: "48px 48px" }} />
      <div className="relative p-6">
        <div className="relative inline-block mb-3">
          <div aria-hidden className="absolute inset-0 -m-3 rounded-full blur-2xl" style={{ background: `${ACCENT}40` }} />
          <div className="relative w-16 h-16 rounded-2xl mx-auto flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${ACCENT}25, ${ACCENT}08)`, border: `1.5px solid ${ACCENT}45` }}>
            <Calendar className="w-8 h-8" style={{ color: ACCENT }} />
          </div>
        </div>
        <h2 className="font-heading text-2xl uppercase tracking-wider mb-2">{t("wc26.league.enterTitle")}</h2>
        <p className="text-[12px] text-white/65 max-w-md mx-auto mb-5 leading-relaxed">
          {t("wc26.league.enterBody")}
        </p>
        <div className="grid grid-cols-3 gap-2 max-w-sm mx-auto mb-5">
          {stats.map((s, i) => (
            <div key={i} className="rounded-2xl border p-3" style={{ borderColor: `${ACCENT}22`, background: `${ACCENT}0d` }}>
              <div className="font-heading text-2xl font-extrabold tabular-nums" style={{ color: ACCENT }}>{s.n}</div>
              <div className="text-[8.5px] font-mono uppercase tracking-widest text-white/45 mt-0.5 leading-tight">{s.l}</div>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={onRegister}
          disabled={busy}
          data-testid="wc26-league-register-btn"
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#FFD700] text-[#020617] font-extrabold px-6 py-3 text-sm uppercase tracking-wider disabled:opacity-50 hover:translate-y-[-1px] transition-transform shadow-lg"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("wc26.league.registerBtn", { pool })}
        </button>
      </div>
    </div>
  );
}

/* ============================================================ */
/* HOME TAB — fixture-driven match-flow state machine           */
/*   + Journey timeline + Mini standings                        */
/* ============================================================ */
function HomeTab({ pool, state, onChanged, userId, onJumpTab, nextCutPct, daysToCut, accent = "#38BDF8" }) {
  const { t } = useT();
  const legs = state.today_legs && state.today_legs.length ? state.today_legs : (state.today_match ? [state.today_match] : []);
  const m = legs[0] || null;
  const day = state.today_day;
  const stage = state.today_stage;
  const playing = !!(day && m && m.status !== "bye");
  return (
    <div className="space-y-4" data-testid="wc26-league-home-tab">
      <JourneyTimeline stages={state.tournament?.stages} todayDay={day} />
      {!day ? (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center" data-testid="wc26-league-no-day">
          <Calendar className="w-6 h-6 mx-auto text-white/50 mb-2" />
          <div className="font-bold">{t("wc26.league.noDay")}</div>
          <div className="text-[12px] text-white/60 mt-1">{t("wc26.league.noDay.body")}</div>
        </div>
      ) : !m ? (
        <IdleCard pool={pool} day={day} stage={stage} onChanged={onChanged} userId={userId} />
      ) : m.status === "bye" ? (
        <ByeCard m={m} />
      ) : (
        <PairCard legs={legs} userId={userId} onChanged={onChanged} />
      )}
      {/* My Standings · next-cut summary (spec §4.11) */}
      {nextCutPct != null && (
        <button
          type="button"
          onClick={() => onJumpTab?.("league")}
          data-testid="wc26-league-home-nextcut"
          className="w-full rounded-2xl border bg-white/5 px-4 py-3 flex items-center justify-between gap-3 text-left hover:bg-white/[0.07] transition-colors"
          style={{ borderColor: `${accent}26` }}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <Flag className="w-4 h-4 shrink-0" style={{ color: accent }} />
            <div className="min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/45">{t("wc26.league.nextCut")}</div>
              <div className="text-[12px] font-bold text-white truncate">
                {t("wc26.league.cutBar", { pct: nextCutPct })}{daysToCut != null ? ` · ${daysToCut}d` : ""}
              </div>
            </div>
          </div>
          <span className="shrink-0 text-[10px] font-mono uppercase tracking-widest" style={{ color: accent }}>{t("wc26.league.tabs.myleague")} →</span>
        </button>
      )}
      {/* Today's real fixtures (always shown on Home, not just when idle) */}
      {day && <TodaysFixturesCard pool={pool} accent={accent} />}
      <MiniStandings pool={pool} userId={userId} me={state.me} onJumpTab={onJumpTab} accent={accent} />
    </div>
  );
}

/* Today's real fixtures side-card (spec §4.11) */
function TodaysFixturesCard({ pool, accent = "#38BDF8" }) {
  const { t } = useT();
  const [fixtures, setFixtures] = useState(null);
  useEffect(() => {
    api.get(`/wc26/today?pool=${pool}`).then((r) => setFixtures(r.data?.fixtures || [])).catch(() => setFixtures([]));
  }, [pool]);
  if (!fixtures || fixtures.length === 0) return null;
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0F172A] overflow-hidden" data-testid="wc26-league-today-fixtures">
      <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-[0.3em] border-b border-white/10 flex items-center gap-1.5" style={{ color: accent }}>
        <Calendar className="w-3 h-3" /> {t("wc26.league.idleTitle")}
      </div>
      <div className="divide-y divide-white/5">
        {fixtures.map((f) => (
          <div key={f.id} className="flex items-center justify-between px-3 py-2 text-[12px]" data-testid={`wc26-league-todayfix-${f.id}`}>
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-base">{flag(f.team_a)}</span>
              <span className="font-bold truncate">{f.team_a}</span>
              <span className="text-white/30 mx-0.5">vs</span>
              <span className="font-bold truncate">{f.team_b}</span>
              <span className="text-base">{flag(f.team_b)}</span>
            </div>
            <span className="shrink-0 font-mono text-[9px] uppercase tracking-widest text-white/40 ml-2">
              {f.group ? `${t("wc26.league.day")} ${f.group}` : (f.stage || "").toUpperCase()}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Rules sheet — quick-jump from the sticky sub-nav (spec §4.11) */
function RulesSheet({ onClose, accent = "#38BDF8" }) {
  const { t } = useT();
  const rules = [1, 2, 3, 4, 5, 6].map((i) => t(`wc26.rules.immersion.${i}`));
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="wc26-league-rules-sheet">
      <div
        className="w-full max-w-md rounded-3xl border bg-[#0B1220] overflow-hidden"
        style={{ borderColor: `${accent}40` }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-3 flex items-center gap-3" style={{ background: `linear-gradient(135deg, ${accent}12, transparent)` }}>
          <div className="relative shrink-0">
            <div aria-hidden className="absolute inset-0 -m-2 rounded-full blur-xl" style={{ background: `${accent}40` }} />
            <div className="relative w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${accent}1a`, border: `1.5px solid ${accent}40` }}>
              <Calendar className="w-5 h-5" style={{ color: accent }} />
            </div>
          </div>
          <div>
            <div className="text-[9px] font-mono uppercase tracking-[0.3em]" style={{ color: accent }}>// {t("wc26.mode.immersion.chapter")}</div>
            <div className="font-heading text-lg uppercase tracking-wider">{t("wc26.mode.immersion.title")}</div>
          </div>
        </div>
        <ul className="px-5 py-4 space-y-2.5">
          {rules.map((r, i) => (
            <li key={i} className="flex items-start gap-3 text-[12.5px] text-white/80 leading-relaxed">
              <span className="mt-[6px] w-1.5 h-1.5 rounded-full shrink-0" style={{ background: accent, boxShadow: `0 0 6px ${accent}90` }} />
              <span>{r}</span>
            </li>
          ))}
        </ul>
        <div className="px-5 pb-5">
          <button
            type="button"
            onClick={onClose}
            data-testid="wc26-league-rules-close"
            className="w-full py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-[12px] font-bold uppercase tracking-wider"
          >
            {t("v2.common.close")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Iter 83 · Wave 3 — render BOTH legs side-by-side + shared chat thread */
function PairCard({ legs, userId, onChanged }) {
  const { t } = useT();
  const first = legs[0];
  const showSidebySide = legs.length === 2;
  return (
    <div className="space-y-3" data-testid="wc26-league-pair">
      <div className="text-[11px] text-white/70 text-center px-2">
        {t("wc26.league.replaying")}{" "}
        <span className="text-white font-bold">{first.team_a} {flag(first.team_a)} vs {flag(first.team_b)} {first.team_b}</span>
        {first.group ? <span className="text-white/50"> · {t("wc26.league.day")} {first.group}</span> : null}
      </div>
      <div className={`grid gap-3 ${showSidebySide ? "md:grid-cols-2" : ""}`}>
        {legs.map((leg) => (
          leg.status === "confirmed" ? <ConfirmedCard key={leg.id} m={leg} userId={userId} />
          : leg.status === "disputed" ? <DisputedCard key={leg.id} m={leg} />
          : <MatchedCard key={leg.id} m={leg} userId={userId} onChanged={onChanged} />
        ))}
      </div>
      <MatchChat matchId={first.id} pairId={first.pair_id} />
    </div>
  );
}

/* Stage progression timeline — past green, current pulsing gold, future dim */
function JourneyTimeline({ stages, todayDay }) {
  const { t } = useT();
  if (!stages?.length) return null;
  const stageKeyMap = {
    "Group Stage":   "wc26.league.stage.group",
    "Round of 32":   "wc26.league.stage.r32",
    "Round of 16":   "wc26.league.stage.r16",
    "Quarterfinals": "wc26.league.stage.qf",
    "Semifinals":    "wc26.league.stage.sf",
    "Final":         "wc26.league.stage.final",
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-black/30 p-3" data-testid="wc26-league-journey">
      <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40 mb-2">{t("wc26.league.journey")}</div>
      <div className="flex items-stretch gap-1">
        {stages.map((s, i) => {
          const day = todayDay ?? 0;
          const isPast    = day > (s.end_day ?? 0);
          const isCurrent = day >= (s.start_day ?? 0) && day <= (s.end_day ?? 0);
          const accent = isPast ? "#10B981" : isCurrent ? "#FFD700" : "#3F3F46";
          const dayInStage = isCurrent ? day - (s.start_day ?? 0) + 1 : 0;
          const label = stageKeyMap[s.name] ? t(stageKeyMap[s.name]) : (s.name.replace(/Stage|Round of /gi, "").trim() || s.name);
          return (
            <div key={s.name + i} className="flex-1 min-w-0" data-testid={`wc26-journey-stage-${i}`}>
              <div className="text-[8px] font-mono uppercase truncate text-center" style={{ color: accent }}>
                {label}
              </div>
              <div className="mt-1 h-1.5 rounded-full overflow-hidden bg-white/5">
                <div
                  className={isCurrent ? "h-full animate-pulse" : "h-full"}
                  style={{
                    width: isPast ? "100%" : isCurrent ? `${Math.min(100, (dayInStage / (s.day_count || 1)) * 100)}%` : "0%",
                    background: accent,
                  }}
                />
              </div>
              {isCurrent && (
                <div className="text-[8px] font-mono text-[#FFD700] text-center mt-0.5">D{dayInStage}/{s.day_count}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* Shard mini-standings — top 3 + you ± 2 (spec §4.11) + "View full table →" */
function MiniStandings({ pool, userId, me, onJumpTab, accent = "#38BDF8" }) {
  const { t } = useT();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!me) return;
    api.get(`/wc26/standings/mine?pool=${pool}`).then((r) => {
      const all = r.data?.rows || [];
      // top 3 + a window of [me-2 .. me+2], de-duped, in rank order
      const top = all.slice(0, 3);
      const myIdx = all.findIndex((x) => x.user_id === userId);
      const window = myIdx >= 0 ? all.slice(Math.max(0, myIdx - 2), myIdx + 3) : [];
      const seen = new Set();
      const merged = [...top, ...window]
        .filter((x) => (seen.has(x.user_id) ? false : (seen.add(x.user_id), true)))
        .sort((a, b) => a.rank - b.rank);
      setRows(merged);
    }).catch(() => setRows([]));
  }, [pool, userId, me]);
  if (!me || !rows || rows.length === 0) return null;
  // detect rank gaps to render an ellipsis divider
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden" data-testid="wc26-league-mini-standings">
      <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-white/50 border-b border-white/10 flex items-center justify-between">
        <span>{t("wc26.league.miniStandings")}</span>
        <Trophy className="w-3 h-3 text-[#FFD700]" />
      </div>
      <div className="divide-y divide-white/5">
        {rows.map((r, i) => {
          const gap = i > 0 && r.rank - rows[i - 1].rank > 1;
          return (
            <React.Fragment key={r.id}>
              {gap && (
                <div className="px-3 py-1 text-center text-[10px] font-mono text-white/25 tracking-[0.3em]">···</div>
              )}
              <div className={`flex items-center justify-between px-3 py-1.5 text-[11px] ${r.user_id === userId ? "bg-[#FFD700]/10" : ""}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-mono text-[10px] text-white/50 w-6 tabular-nums">#{r.rank}</span>
                  <span className="font-bold truncate">@{r.username}</span>
                  {r.user_id === userId && <span className="text-[8px] font-mono text-[#FFD700] uppercase">{t("wc26.league.you")}</span>}
                </div>
                <span className="text-[#FFD700] font-extrabold tabular-nums">{r.points}p</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
      {onJumpTab && (
        <button
          type="button"
          onClick={() => onJumpTab("league")}
          data-testid="wc26-league-view-full-table"
          className="w-full px-3 py-2 text-[10px] font-mono uppercase tracking-widest border-t border-white/10 hover:bg-white/5 transition-colors"
          style={{ color: accent }}
        >
          {t("wc26.league.viewFullTable")}
        </button>
      )}
    </div>
  );
}

/* idle → show fixtures + "Roll my team" button → cinematic reveal overlay */
function IdleCard({ pool, day, stage, onChanged, userId }) {
  const { t } = useT();
  const [fixtures, setFixtures] = useState([]);
  const [rolling, setRolling] = useState(false);
  const [waiting, setWaiting] = useState(false);   // odd-one-out → in the queue
  const [reveal, setReveal] = useState(null); // { teams, match } once rolled

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/wc26/today?pool=${pool}`);
        setFixtures(data.fixtures || []);
      } catch { /* noop */ }
    })();
  }, [pool]);

  const allTeams = fixtures.flatMap((f) => [f.team_a, f.team_b]).filter((tm) => tm && tm !== "TBD");

  const roll = async () => {
    setRolling(true);
    try {
      const { data } = await api.post(`/wc26/today/roll`, { pool });
      if (!data?.match || data?.status === "waiting") {
        // Odd one out — no bye, no points. Wait in the queue for an opponent.
        setWaiting(true);
        setRolling(false);
        toast.message(data?.message || "You're in the queue — waiting for an opponent to join.");
        return;
      }
      // data.match carries my drawn team + opponent → drive the reveal sequence
      setWaiting(false);
      setReveal({ teams: allTeams, match: data.match });
    } catch (e) {
      toast.error(formatApiError(e));
      setRolling(false);
    }
  };

  // Auto-poll while waiting so the player is paired automatically when an
  // opponent becomes available — then play the reveal sequence.
  useEffect(() => {
    if (!waiting) return undefined;
    let stopped = false;
    const id = setInterval(async () => {
      try {
        const { data } = await api.post(`/wc26/today/roll`, { pool });
        if (!stopped && data?.match && data?.status !== "waiting") {
          setWaiting(false);
          setReveal({ teams: allTeams, match: data.match });
        }
      } catch { /* keep waiting silently */ }
    }, 6000);
    return () => { stopped = true; clearInterval(id); };
  }, [waiting, pool]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="rounded-3xl border border-[#38BDF8]/30 bg-gradient-to-br from-[#0F172A] to-[#020617] p-5" data-testid="wc26-league-idle">
      <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#38BDF8]">// {t("wc26.league.day")} {day} · {stage?.name || t("wc26.league.stage.group")}</div>
      <h2 className="font-heading text-2xl uppercase tracking-wider mt-1">{t("wc26.league.idleTitle")}</h2>
      <div className="mt-3 space-y-2">
        {fixtures.length === 0 ? (
          <div className="text-[12px] text-white/60">{t("wc26.league.idleNoFix")}</div>
        ) : (
          fixtures.map((f) => (
            <div key={f.id} data-testid={`wc26-league-fixture-${f.id}`} className="flex items-center justify-between rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-[13px]">
              <div className="flex items-center gap-2">
                <span className="text-lg">{flag(f.team_a)}</span>
                <span className="font-bold">{f.team_a}</span>
                <span className="text-white/40 mx-1">vs</span>
                <span className="font-bold">{f.team_b}</span>
                <span className="text-lg">{flag(f.team_b)}</span>
              </div>
              <span className="font-mono text-[9px] uppercase tracking-widest text-white/40">{f.group ? `${t("wc26.league.day")} ${f.group}` : f.stage?.toUpperCase()}</span>
            </div>
          ))
        )}
      </div>
      <p className="text-[11px] text-white/60 mt-4 text-center">
        {waiting ? "You're in the queue — waiting for an opponent to join. Your match will appear here automatically." : t("wc26.league.idleHelp")}
      </p>
      <button
        type="button"
        onClick={roll}
        disabled={rolling || waiting || fixtures.length === 0}
        data-testid="wc26-league-roll-btn"
        className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#FFD700] text-[#020617] font-extrabold px-5 py-3 text-sm uppercase tracking-wider disabled:opacity-50"
      >
        {(rolling || waiting)
          ? <><Loader2 className="w-4 h-4 animate-spin" /> {waiting ? "Waiting for an opponent…" : ""}</>
          : <><Shuffle className="w-4 h-4" /> {t("wc26.league.rollBtn")}</>}
      </button>

      {reveal && (
        <RevealOverlay
          teams={reveal.teams}
          match={reveal.match}
          userId={userId}
          onDone={async () => { setReveal(null); setRolling(false); await onChanged?.(); }}
        />
      )}
    </div>
  );
}

/* ============================================================ */
/* RevealOverlay — cinematic daily team + opponent reveal        */
/*   rolling (flag slot-machine) → your team → fixture →         */
/*   opponent reveal | bye | waiting-for-opponent                */
/* ============================================================ */
function RevealOverlay({ teams, match, userId, onDone }) {
  const { t } = useT();
  const ACCENT = "#38BDF8";
  const pool = (teams && teams.length) ? teams : ["🏳️"];

  // Derive outcome from the real roll response
  const iAmHome = match ? match.home_user === userId : true;
  const isBye   = !!match && (match.status === "bye" || (!match.away_user && match.home_user === userId && match.leg == null));
  const myTeam  = match ? (iAmHome ? (match.home_team || match.team_a) : (match.away_team || match.team_b)) : null;
  const oppTeam = match ? (iAmHome ? (match.away_team || match.team_b) : (match.home_team || match.team_a)) : null;
  const oppUser = match ? (iAmHome ? match.away_username : match.home_username) : null;
  const group   = match?.group;
  const waiting = !isBye && match && !oppUser;

  const [phase, setPhase] = useState("rolling"); // rolling → team → fixture → opp
  const [spinIdx, setSpinIdx] = useState(0);
  const [oppSpin, setOppSpin] = useState(true);

  // Slot-machine flag cycle during the "rolling" phase
  useEffect(() => {
    if (phase !== "rolling") return undefined;
    const tk = setInterval(() => setSpinIdx((i) => i + 1), 70);
    return () => clearInterval(tk);
  }, [phase]);

  // Phase timeline
  useEffect(() => {
    const timers = [];
    timers.push(setTimeout(() => setPhase("team"), 1400));
    timers.push(setTimeout(() => setPhase("fixture"), 2700));
    timers.push(setTimeout(() => setPhase("opp"), 4100));
    // brief opponent "matching" beat, then settle
    timers.push(setTimeout(() => setOppSpin(false), 4900));
    return () => timers.forEach(clearTimeout);
  }, []);

  const spinTeam = pool[spinIdx % pool.length];
  const showSkip = phase !== "opp" || oppSpin;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#020617]/95 backdrop-blur-md" data-testid="wc26-league-reveal">
      <div aria-hidden className="absolute inset-0" style={{ background: `radial-gradient(120% 80% at 50% 30%, ${ACCENT}1f 0%, transparent 60%)` }} />
      <div className="relative w-full max-w-sm text-center">

        {/* PHASE: rolling — slot machine */}
        {phase === "rolling" && (
          <div data-testid="wc26-reveal-rolling">
            <div className="text-[10px] font-mono uppercase tracking-[0.4em] mb-6" style={{ color: ACCENT }}>{t("wc26.league.reveal.drawing")}</div>
            <div className="relative mx-auto w-40 h-40 rounded-3xl flex items-center justify-center overflow-hidden" style={{ background: `${ACCENT}10`, border: `1.5px solid ${ACCENT}40` }}>
              <div aria-hidden className="absolute inset-0 -m-6 blur-2xl rounded-full" style={{ background: `${ACCENT}30` }} />
              <span className="relative text-7xl animate-pulse">{flag(spinTeam)}</span>
            </div>
            <Shuffle className="w-5 h-5 mx-auto mt-5 animate-spin" style={{ color: ACCENT }} />
          </div>
        )}

        {/* PHASE: team — your team lands */}
        {phase === "team" && (
          <div data-testid="wc26-reveal-team" className="animate-[fadeIn_.3s_ease]">
            <div className="text-[10px] font-mono uppercase tracking-[0.4em] mb-5" style={{ color: ACCENT }}>{t("wc26.league.reveal.yourTeam")}</div>
            <div className="relative mx-auto w-44 h-44 rounded-3xl flex items-center justify-center" style={{ background: `${ACCENT}14`, border: `2px solid ${ACCENT}` }}>
              <div aria-hidden className="absolute inset-0 -m-8 blur-3xl rounded-full" style={{ background: `${ACCENT}45` }} />
              <span className="relative text-8xl drop-shadow-2xl">{flag(myTeam)}</span>
            </div>
            <div className="font-heading text-3xl uppercase tracking-wider mt-5">{myTeam || "—"}</div>
          </div>
        )}

        {/* PHASE: fixture — the real match you're replaying */}
        {phase === "fixture" && (
          <div data-testid="wc26-reveal-fixture" className="animate-[fadeIn_.3s_ease]">
            <div className="text-[10px] font-mono uppercase tracking-[0.4em] mb-6" style={{ color: ACCENT }}>{t("wc26.league.reveal.fixture")}</div>
            <div className="flex items-center justify-center gap-4">
              <div className="text-center">
                <div className="text-6xl drop-shadow-xl">{flag(myTeam)}</div>
                <div className="text-[11px] font-bold mt-2 max-w-[88px] truncate">{myTeam}</div>
              </div>
              <div className="font-heading text-2xl text-white/30">VS</div>
              <div className="text-center">
                <div className="text-6xl drop-shadow-xl">{flag(oppTeam)}</div>
                <div className="text-[11px] font-bold mt-2 max-w-[88px] truncate">{oppTeam}</div>
              </div>
            </div>
            {group && <div className="mt-5 inline-block text-[10px] font-mono uppercase tracking-widest text-white/50 px-2.5 py-1 rounded-full bg-white/5 border border-white/10">{t("wc26.league.day")} {group}</div>}
          </div>
        )}

        {/* PHASE: opponent / bye / waiting */}
        {phase === "opp" && (
          <div data-testid="wc26-reveal-opp" className="animate-[fadeIn_.3s_ease]">
            {isBye ? (
              <>
                <Crown className="w-12 h-12 mx-auto mb-3" style={{ color: "#10B981" }} />
                <div className="font-heading text-2xl uppercase tracking-wider text-[#10B981]">{t("wc26.league.reveal.bye")}</div>
                <div className="text-[12px] text-white/60 mt-2 max-w-xs mx-auto">{t("wc26.league.reveal.byeBody")}</div>
              </>
            ) : waiting ? (
              <>
                <Loader2 className="w-10 h-10 mx-auto mb-3 animate-spin" style={{ color: ACCENT }} />
                <div className="font-heading text-xl uppercase tracking-wider">{t("wc26.league.reveal.waiting")}</div>
                <div className="text-[12px] text-white/55 mt-2 max-w-xs mx-auto leading-relaxed">{t("wc26.league.reveal.waitingBody")}</div>
              </>
            ) : oppSpin ? (
              <>
                <div className="text-[10px] font-mono uppercase tracking-[0.4em] mb-5" style={{ color: ACCENT }}>{t("wc26.league.reveal.matching")}</div>
                <div className="relative mx-auto w-24 h-24 rounded-full flex items-center justify-center" style={{ background: `${ACCENT}12`, border: `1.5px solid ${ACCENT}40` }}>
                  <span className="text-4xl font-heading text-white/40 animate-pulse">?</span>
                </div>
              </>
            ) : (
              <>
                <div className="text-[10px] font-mono uppercase tracking-[0.4em] mb-5" style={{ color: ACCENT }}>{t("wc26.league.reveal.opponent")}</div>
                <div className="relative mx-auto w-24 h-24 rounded-full flex items-center justify-center" style={{ background: `${ACCENT}1a`, border: `2px solid ${ACCENT}` }}>
                  <div aria-hidden className="absolute inset-0 -m-4 blur-2xl rounded-full" style={{ background: `${ACCENT}40` }} />
                  <span className="relative font-heading text-2xl font-extrabold" style={{ color: ACCENT }}>
                    {(oppUser || "?").slice(0, 2).toUpperCase()}
                  </span>
                </div>
                <div className="font-heading text-2xl mt-4">@{oppUser}</div>
                <div className="text-[11px] text-white/50 mt-1">{flag(oppTeam)} {oppTeam}</div>
              </>
            )}
            {!oppSpin && (
              <button
                type="button"
                onClick={onDone}
                data-testid="wc26-reveal-continue"
                className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#FFD700] text-[#020617] font-extrabold px-5 py-3 text-sm uppercase tracking-wider hover:translate-y-[-1px] transition-transform"
              >
                {t("wc26.league.reveal.continue")}
              </button>
            )}
          </div>
        )}

        {showSkip && (
          <button
            type="button"
            onClick={onDone}
            data-testid="wc26-reveal-skip"
            className="absolute -top-2 right-0 text-[10px] font-mono uppercase tracking-widest text-white/40 hover:text-white/80"
          >
            skip
          </button>
        )}
      </div>
    </div>
  );
}

/* Single-leg match card (ready → submit → submitted) — Iter 83. */
function MatchedCard({ m, userId, onChanged }) {
  const { t } = useT();
  const iAmHome = m.home_user === userId;
  const myTeam  = m.home_team || (iAmHome ? m.team_a : m.team_b);
  const oppTeam = m.away_team || (iAmHome ? m.team_b : m.team_a);
  const oppUser = iAmHome ? m.away_username : m.home_username;
  const mySub = (m.subs || {})[userId];
  const [my, setMy] = useState(0);
  const [opp, setOpp] = useState(0);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) { toast.error(t("wc26.league.screenshotReq")); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("my_score", String(my));
      fd.append("opp_score", String(opp));
      fd.append("screenshot", file);
      await api.post(`/wc26/matches/${m.id}/submit`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("wc26.league.scoreSubmitted"));
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const legBadge = m.leg ? (m.home_team === m.team_a && m.leg === 1 ? t("wc26.league.legHome", { n: m.leg }) : (m.leg === 2 ? t("wc26.league.legAway", { n: m.leg }) : t("wc26.league.leg", { n: m.leg }))) : "";

  return (
    <div className="rounded-3xl border border-[#38BDF8]/40 bg-gradient-to-br from-[#0F172A] to-[#0B1B3A] overflow-hidden" data-testid={`wc26-league-match-${m.id}`}>
      <div className="px-4 py-2 text-[10px] font-mono uppercase tracking-widest border-b border-white/10 flex items-center justify-between" style={{ color: "#38BDF8" }}>
        <span className="inline-flex items-center gap-1.5">
          <span className="px-1.5 py-[1px] rounded-full text-[8px] font-extrabold" style={{ background: iAmHome ? "#38BDF833" : "#ffffff15", color: iAmHome ? "#7DD3FC" : "#A1A1AA" }}>
            {iAmHome ? "HOME" : "AWAY"}
          </span>
          {t("wc26.league.day")} {m.day}{legBadge ? ` · ${legBadge}` : ""}
        </span>
        <span className="text-white/40">{m.status}</span>
      </div>
      <div className="grid grid-cols-3 items-center px-4 py-5">
        <div className="text-center">
          <div className="text-5xl drop-shadow-lg">{flag(myTeam)}</div>
          <div className="text-[12px] font-bold mt-1.5 truncate">{myTeam}</div>
          <div className="text-[9px] font-mono uppercase tracking-widest text-[#38BDF8] mt-0.5">{t("wc26.league.you")}</div>
        </div>
        <div className="text-center">
          <div className="text-white/20 font-heading font-extrabold text-2xl">VS</div>
          <div aria-hidden className="mx-auto mt-1 w-8 h-px bg-white/10" />
        </div>
        <div className="text-center">
          <div className="text-5xl drop-shadow-lg">{flag(oppTeam)}</div>
          <div className="text-[12px] font-bold mt-1.5 truncate">{oppTeam}</div>
          <div className="text-[9px] font-mono uppercase tracking-widest text-white/50 mt-0.5 truncate">@{oppUser || t("wc26.league.opponent")}</div>
        </div>
      </div>

      {!mySub && (
        <div className="px-4 pb-4 border-t border-white/10 pt-3" data-testid="wc26-league-submit-form">
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-2">
            {t("wc26.league.coordinate")}
          </div>
          <div className="grid grid-cols-3 items-center gap-2">
            <div className="text-center">
              <div className="text-2xl">{flag(myTeam)}</div>
              <input
                type="number" min={0} max={99}
                value={my}
                onChange={(e) => setMy(parseInt(e.target.value || "0", 10))}
                data-testid="wc26-league-my-score"
                style={{ fontSize: "16px" }}
                className="mt-1 w-full bg-black/40 border border-white/10 rounded-lg text-center text-xl font-extrabold py-1.5"
              />
            </div>
            <div className="text-center text-white/40 font-bold">vs</div>
            <div className="text-center">
              <div className="text-2xl">{flag(oppTeam)}</div>
              <input
                type="number" min={0} max={99}
                value={opp}
                onChange={(e) => setOpp(parseInt(e.target.value || "0", 10))}
                data-testid="wc26-league-opp-score"
                style={{ fontSize: "16px" }}
                className="mt-1 w-full bg-black/40 border border-white/10 rounded-lg text-center text-xl font-extrabold py-1.5"
              />
            </div>
          </div>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="wc26-league-screenshot"
            className="mt-3 block w-full text-[10px] text-white file:mr-2 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:text-[9px] file:font-extrabold file:uppercase file:tracking-widest file:bg-[#FFD700] file:text-[#020617]"
          />
          {file && (
            <div className="mt-1 text-[10px] font-mono text-emerald-300 truncate" data-testid="wc26-league-screenshot-name">
              <Check className="inline w-3 h-3 mr-0.5" /> {file.name}
            </div>
          )}
          <button
            type="button"
            onClick={submit}
            disabled={busy || !file}
            data-testid="wc26-league-submit-btn"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#FFD700] text-[#020617] font-extrabold py-2.5 text-sm uppercase tracking-wider disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {t("wc26.league.submitResult")}
          </button>
        </div>
      )}

      {mySub && m.status === "submitted" && (
        <div className="px-4 pb-4 text-center text-[12px] text-amber-300" data-testid="wc26-league-awaiting">
          <Loader2 className="inline w-3.5 h-3.5 animate-spin mr-1" />
          {t("wc26.league.submittedLine", { h: mySub.home_score, a: mySub.away_score })}
        </div>
      )}
    </div>
  );
}

/* Iter 83 · Wave 3 — pair-scoped chat with 6 quick-reply chips. */
const QUICK_REPLY_KEYS = [
  "wc26.league.chip.ready",
  "wc26.league.chip.5min",
  "wc26.league.chip.10min",
  "wc26.league.chip.gg",
  "wc26.league.chip.tomorrow",
  "wc26.league.chip.lag",
];

function MatchChat({ matchId }) {
  const { t } = useT();
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const load = async () => {
    try {
      const { data } = await api.get(`/wc26/matches/${matchId}/chat`);
      setMsgs(data.messages || []);
    } catch (_) {}
  };
  useEffect(() => { if (matchId) load(); }, [matchId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!matchId) return undefined;
    const id = setInterval(load, 6000);  // light poll
    return () => clearInterval(id);
  }, [matchId]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async (text) => {
    const v = (text ?? input).trim();
    if (!v) return;
    setBusy(true);
    try {
      await api.post(`/wc26/matches/${matchId}/chat`, { content: v });
      setInput("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-3xl border border-white/10 bg-[#0F172A] overflow-hidden" data-testid="wc26-league-chat">
      <div className="px-4 py-2 text-[10px] font-mono uppercase tracking-[0.3em] text-white/60 border-b border-white/10">
        // {t("wc26.league.chat.title")}
      </div>
      <div className="max-h-56 overflow-y-auto px-3 py-2 space-y-1.5">
        {msgs.length === 0 && (
          <div className="text-center text-[11px] text-white/40 py-3">{t("wc26.league.chat.empty")}</div>
        )}
        {msgs.map((mm) => {
          const mine = mm.user_id === user?.id;
          return (
            <div key={mm.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[80%] rounded-2xl px-3 py-1.5 text-[12px] leading-snug ${
                  mine ? "bg-[#FFD700] text-[#020617]" : "bg-white/10 text-white"
                }`}
                data-testid={`wc26-league-chat-msg-${mm.id}`}
              >
                {!mine && <div className="text-[9px] font-mono uppercase tracking-widest opacity-70 mb-0.5">@{mm.username}</div>}
                {mm.content}
              </div>
            </div>
          );
        })}
      </div>
      <div className="px-3 pt-2 flex items-center gap-1.5 overflow-x-auto no-scrollbar">
        {QUICK_REPLY_KEYS.map((qrKey) => {
          const label = t(qrKey);
          return (
            <button
              key={qrKey}
              type="button"
              onClick={() => send(label)}
              disabled={busy}
              data-testid={`wc26-league-chat-chip-${qrKey.split(".").pop()}`}
              className="shrink-0 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] hover:bg-[#FFD700]/15 hover:border-[#FFD700]/40 disabled:opacity-50"
            >{label}</button>
          );
        })}
      </div>
      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="flex items-center gap-2 px-3 py-2 border-t border-white/10"
        data-testid="wc26-league-chat-form"
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t("wc26.league.chat.placeholder")}
          data-testid="wc26-league-chat-input"
          maxLength={500}
          style={{ fontSize: "16px" }}
          className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-[13px] placeholder-white/30 focus:outline-none focus:border-[#FFD700]/40"
        />
        <button
          type="submit"
          disabled={busy || !input.trim()}
          data-testid="wc26-league-chat-send"
          className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#FFD700] text-[#020617] disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 rotate-90" />}
        </button>
      </form>
    </div>
  );
}

function ConfirmedCard({ m, userId }) {
  const { t } = useT();
  const iAmHome = m.home_user === userId;
  const myPts = iAmHome ? m.points_home : m.points_away;
  const won = (iAmHome ? m.home_score > m.away_score : m.away_score > m.home_score);
  const draw = m.home_score === m.away_score;
  const tag = draw ? t("wc26.league.draw") : (won ? t("wc26.league.victory") : t("wc26.league.defeat"));
  const color = draw ? "#FBBF24" : (won ? "#10B981" : "#EF4444");
  return (
    <div className="rounded-3xl border bg-[#0F172A] overflow-hidden" style={{ borderColor: `${color}55` }} data-testid="wc26-league-confirmed">
      <div className="px-4 py-2 text-[10px] font-mono uppercase tracking-widest border-b border-white/10" style={{ color }}>
        {t("wc26.league.day")} {m.day} · {t("wc26.league.confirmedTag")}
      </div>
      <div className="p-5 text-center">
        <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color }}>// {tag}</div>
        <div className="text-4xl font-extrabold tabular-nums mt-1">
          {m.home_score} <span className="text-white/30">–</span> {m.away_score}
        </div>
        <div className="text-[12px] text-white/60 mt-2">{m.team_a} {flag(m.team_a)} vs {flag(m.team_b)} {m.team_b}</div>
        <div className="mt-3 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#FFD700]/15 border border-[#FFD700]/40 text-[#FFD700] text-[11px] font-extrabold uppercase tracking-widest">
          +{myPts ?? 0} {t("wc26.league.points")}
        </div>
      </div>
    </div>
  );
}

function ByeCard({ m }) {
  const { t } = useT();
  return (
    <div className="rounded-3xl border border-[#10B981]/40 bg-[#0F172A] p-5 text-center" data-testid="wc26-league-bye">
      <Crown className="w-8 h-8 mx-auto text-[#10B981] mb-1" />
      <div className="font-heading text-xl uppercase tracking-wider">{t("wc26.league.byeDay")}</div>
      <div className="text-[12px] text-white/60 mt-1">{t("wc26.league.byeFooter", { pts: m.points_home ?? 3 })}</div>
    </div>
  );
}

function DisputedCard({ m }) {
  const { t } = useT();
  return (
    <div className="rounded-3xl border border-rose-500/40 bg-[#0F172A] p-5 text-center" data-testid="wc26-league-disputed">
      <Flag className="w-6 h-6 mx-auto text-rose-400 mb-1" />
      <div className="font-bold">{t("wc26.league.dispTitle")}</div>
      <div className="text-[12px] text-white/60 mt-1">{t("wc26.league.dispBody")}</div>
    </div>
  );
}

/* ============================================================ */
/* MY LEAGUE TAB — full shard standings + survival bar          */
/* ============================================================ */
function MyLeagueTab({ pool, userId, advancePct = 30 }) {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  useEffect(() => {
    api.get(`/wc26/standings/mine?pool=${pool}`).then((r) => setData(r.data)).catch(() => {});
  }, [pool]);
  if (!data) return <Loader2 className="w-5 h-5 animate-spin mx-auto mt-6 text-[#FFD700]" />;
  if (!data.registered) return <div className="text-center text-sm text-white/60">{t("wc26.league.registerFirst")}</div>;
  // Cut-line math must use ACTIVE players only (matches backend keep_n =
  // ceil(activeCount * pct/100)); eliminated rows would skew the safe zone.
  const total = data.rows.filter((r) => r.status === "active").length;
  // advancePct === null/undefined means we're on the FINAL stage — backend keeps
  // exactly ONE survivor (the champion), so don't fall back to a % cut here.
  const isFinalStage = advancePct == null;
  const cutoff = isFinalStage ? 1 : Math.max(1, Math.ceil(total * (advancePct / 100)));
  const me = data.rows.find((r) => r.user_id === userId);
  const myRank = me?.rank ?? null;
  const inSafe = myRank !== null && myRank <= cutoff;
  const q = search.trim().toLowerCase();
  const filtered = data.rows.filter((r) => {
    if (q && !(r.username || "").toLowerCase().includes(q)) return false;
    if (filter === "all") return true;
    if (filter === "safe") return r.rank <= cutoff && r.status === "active";
    if (filter === "cut") return r.rank > cutoff && r.status === "active";
    if (filter === "out") return r.status === "eliminated";
    return true;
  });
  return (
    <div className="space-y-3" data-testid="wc26-league-myleague">
      <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#0F172A] to-[#020617] p-4" data-testid="wc26-league-survival">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40">{t("wc26.league.survival")}</div>
        <div className="flex items-end justify-between mt-1">
          <div>
            <div className="text-[11px] text-white/70">{isFinalStage ? `Grand Final · Top 1 of ${total} takes the crown` : t("wc26.league.shardLine", { n: data.shard_num, total, pct: advancePct })}</div>
            <div className={`text-lg font-extrabold mt-0.5 ${inSafe ? "text-emerald-400" : "text-rose-400"}`}>
              {myRank ? (inSafe ? t("wc26.league.safeLine", { rank: myRank, cutoff }) : t("wc26.league.belowCut", { rank: myRank, cutoff })) : "—"}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[9px] font-mono uppercase tracking-widest text-[#FFD700]">{t("wc26.league.yourPts")}</div>
            <div className="text-2xl font-extrabold tabular-nums text-[#FFD700]">{me?.points ?? 0}</div>
          </div>
        </div>
        <div className="mt-3 relative h-4 rounded-full bg-white/5 border border-white/10 overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-500/60 to-emerald-400/40"
            style={{ width: `${(cutoff / Math.max(1, total)) * 100}%` }}
            data-testid="wc26-league-safe-zone"
          />
          <div
            className="absolute inset-y-0 bg-rose-500/30"
            style={{ left: `${(cutoff / Math.max(1, total)) * 100}%`, right: 0 }}
          />
          {myRank && total > 0 && (
            <div
              className="absolute top-1/2 -translate-y-1/2 w-1 h-6 bg-[#FFD700] rounded shadow-[0_0_8px_rgba(255,215,0,0.8)]"
              style={{ left: `${Math.min(99, ((myRank - 1) / Math.max(1, total - 1)) * 100)}%` }}
              data-testid="wc26-league-my-marker"
            />
          )}
          <div
            className="absolute top-0 bottom-0 w-px bg-white/40"
            style={{ left: `${(cutoff / Math.max(1, total)) * 100}%` }}
          />
        </div>
        <div className="flex justify-between text-[8px] font-mono uppercase tracking-widest text-white/40 mt-1">
          <span>#1</span>
          <span className="text-emerald-300">{t("wc26.league.cutMarker", { cutoff })}</span>
          <span>#{total}</span>
        </div>
      </div>

      <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar">
        {[
          { id: "all",  label: t("wc26.league.filter.all"),  color: null },
          { id: "safe", label: t("wc26.league.filter.safe"), color: "#10B981" },
          { id: "cut",  label: t("wc26.league.filter.cut"),  color: "#F43F5E" },
          { id: "out",  label: t("wc26.league.filter.out"),  color: "#71717A" },
        ].map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            data-testid={`wc26-league-myleague-filter-${f.id}`}
            className={`shrink-0 px-2.5 py-1 rounded-full font-mono text-[10px] uppercase tracking-widest border transition-colors ${
              filter === f.id
                ? "text-[#020617] border-transparent"
                : "text-white/70 border-white/10 hover:border-white/30"
            }`}
            style={filter === f.id ? { background: f.color || "#FFD700" } : {}}
          >{f.label}</button>
        ))}
      </div>

      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t("wc26.league.searchPlayer")}
        data-testid="wc26-league-myleague-search"
        className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-[12px] placeholder-white/30 focus:outline-none focus:border-[#FFD700]/40"
      />

      <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden" data-testid="wc26-league-standings">
        <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-white/50 border-b border-white/10">
          {t("wc26.league.shardShown", { n: data.shard_num, shown: filtered.length, total })}
        </div>
        <div className="max-h-[55vh] overflow-y-auto divide-y divide-white/5">
          {filtered.map((r) => {
            const isMe = r.user_id === userId;
            const status = r.status === "eliminated" ? "out" : (r.rank <= cutoff ? "safe" : "cut");
            const statusColor = status === "safe" ? "#10B981" : status === "cut" ? "#F43F5E" : "#71717A";
            const statusLabel = status === "safe" ? t("wc26.league.statusSafe") : status === "cut" ? t("wc26.league.statusCut") : t("wc26.league.statusOut");
            return (
              <div
                key={r.id}
                className={`flex items-center justify-between px-3 py-2 text-[12px] ${isMe ? "bg-[#FFD700]/10" : ""}`}
                data-testid={`wc26-league-row-${r.user_id}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-mono text-[10px] text-white/50 w-7 tabular-nums">#{r.rank}</span>
                  <span className="font-bold truncate">@{r.username}</span>
                  {isMe && <span className="text-[8px] font-mono text-[#FFD700] uppercase">{t("wc26.league.you")}</span>}
                </div>
                <div className="flex items-center gap-2 font-mono text-[10px]">
                  <span className="text-white/60 tabular-nums">W{r.wins} D{r.draws} L{r.losses}</span>
                  <span className="text-white/40 tabular-nums w-7 text-right">{r.goal_diff >= 0 ? "+" : ""}{r.goal_diff}</span>
                  <span className="text-[#FFD700] font-extrabold tabular-nums w-9 text-right">{r.points}p</span>
                  <span
                    className="text-[9px] font-extrabold uppercase rounded-full px-1.5 py-0.5"
                    style={{ background: `${statusColor}22`, color: statusColor }}
                  >{statusLabel}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ============================================================ */
/* TODAY TAB — all in-shard matches grouped under each real fixture */
/* ============================================================ */
function TodayTab({ pool, userId }) {
  const { t } = useT();
  const [search] = useSearchParams();
  const asOf = search.get("as_of") || null;  // admin preview override
  const [data, setData] = useState(null);
  const reload = useCallback(() => {
    const qs = asOf ? `&as_of=${asOf}` : "";
    api.get(`/wc26/today/matches?pool=${pool}${qs}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ groups: [], stats: {} }));
  }, [pool, asOf]);
  useEffect(() => { reload(); const id = setInterval(reload, 15000); return () => clearInterval(id); }, [reload]);
  if (!data) return <Loader2 className="w-5 h-5 animate-spin mx-auto mt-6 text-[#FFD700]" />;
  const { groups, stats, date } = data;
  if (!groups || groups.length === 0) {
    return <div className="text-center text-sm text-white/60" data-testid="wc26-league-today-empty">{t("wc26.league.noDay")}</div>;
  }
  return (
    <div className="space-y-3" data-testid="wc26-league-today">
      {asOf && (
        <div className="rounded-lg bg-amber-500/15 border border-amber-500/40 px-3 py-1.5 text-[11px] text-amber-300 inline-flex items-center gap-1.5" data-testid="wc26-league-today-preview-banner">
          <Eye className="w-3 h-3" /> {t("wc26.league.preview", { date: asOf })}
        </div>
      )}
      <div className="rounded-2xl bg-[#0F172A] border border-white/10 p-3">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40">// {date}</div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 mt-2">
          <StatChip l={t("wc26.league.statTotal")}      v={stats?.total ?? 0}      c="#FFD700" />
          <StatChip l={t("wc26.league.statScheduled")}  v={stats?.scheduled ?? 0}  c="#A1A1AA" />
          <StatChip l={t("wc26.league.statLive")}       v={stats?.playing ?? 0}    c="#10B981" pulse />
          <StatChip l={t("wc26.league.statSubmitted")}  v={stats?.submitted ?? 0}  c="#FBBF24" />
          <StatChip l={t("wc26.league.statConfirmed")}  v={stats?.confirmed ?? 0}  c="#10B981" />
          <StatChip l={t("wc26.league.statDisputed")}   v={stats?.disputed ?? 0}   c="#EF4444" />
        </div>
      </div>
      {groups.map((g) => (
        <FixtureGroupCard key={g.fixture.id} g={g} userId={userId} />
      ))}
    </div>
  );
}

function StatChip({ l, v, c, pulse }) {
  return (
    <div className="rounded-lg bg-white/5 border border-white/5 p-1.5 text-center">
      <div className="text-[8px] font-mono uppercase tracking-widest text-white/40">{l}</div>
      <div
        className={`text-base font-extrabold tabular-nums ${pulse && v > 0 ? "animate-pulse" : ""}`}
        style={{ color: v > 0 ? c : "#3F3F46" }}
      >{v ?? 0}</div>
    </div>
  );
}

function FixtureGroupCard({ g, userId }) {
  const { t } = useT();
  const f = g.fixture;
  const matches = g.matches || [];
  const isMine = matches.some((m) => m.home_user === userId || m.away_user === userId);
  return (
    <div
      className={`rounded-2xl border bg-[#0F172A] overflow-hidden ${isMine ? "border-[#FFD700]/50 shadow-[0_0_20px_-8px_rgba(255,215,0,0.4)]" : "border-white/10"}`}
      data-testid={`wc26-league-fixgroup-${f.id}`}
    >
      <div className="px-3 py-2 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-lg">{flag(f.team_a)}</span>
          <span className="font-bold truncate">{f.team_a}</span>
          <span className="text-white/40 mx-1">vs</span>
          <span className="font-bold truncate">{f.team_b}</span>
          <span className="text-lg">{flag(f.team_b)}</span>
        </div>
        <span className="shrink-0 font-mono text-[9px] uppercase tracking-widest text-white/40 ml-2">
          {f.group ? `${t("wc26.league.day")} ${f.group}` : f.stage?.toUpperCase()}
        </span>
      </div>
      {matches.length === 0 ? (
        <div className="px-3 py-3 text-[11px] text-white/40 text-center">{t("wc26.league.noShard")}</div>
      ) : (
        <div className="divide-y divide-white/5">
          {matches.map((m) => (
            <MatchRow key={m.id} m={m} userId={userId} />
          ))}
        </div>
      )}
    </div>
  );
}

function MatchRow({ m, userId }) {
  const { t } = useT();
  const isMine = m.home_user === userId || m.away_user === userId;
  const STATUS = {
    scheduled: { color: "#A1A1AA", label: t("wc26.league.statusScheduled") },
    playing:   { color: "#10B981", label: t("wc26.league.statusLive"), pulse: true },
    submitted: { color: "#FBBF24", label: t("wc26.league.statusAwait") },
    confirmed: { color: "#10B981", label: t("wc26.league.statusConf") },
    disputed:  { color: "#EF4444", label: t("wc26.league.statusDisp") },
    forfeit:   { color: "#71717A", label: t("wc26.league.statusForf") },
    bye:       { color: "#3B82F6", label: t("wc26.league.statusBye") },
  };
  const s = STATUS[m.status] || { color: "#71717A", label: m.status };
  return (
    <div
      className={`flex items-center justify-between px-3 py-2 text-[12px] ${isMine ? "bg-[#FFD700]/10" : ""}`}
      data-testid={`wc26-league-today-match-${m.id}`}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <span className="font-bold truncate">@{m.home_username}</span>
        <span className="text-base">{flag(m.team_a)}</span>
        <span className="text-white/40">vs</span>
        <span className="text-base">{flag(m.team_b)}</span>
        <span className="font-bold truncate">@{m.away_username || "—"}</span>
      </div>
      <div className="shrink-0 flex items-center gap-2">
        {m.home_score !== null && m.away_score !== null && (
          <span className="font-mono tabular-nums text-[12px]">{m.home_score}-{m.away_score}</span>
        )}
        <span
          className={`text-[9px] font-mono uppercase tracking-widest rounded-full px-1.5 py-0.5 ${s.pulse ? "animate-pulse" : ""}`}
          style={{ background: `${s.color}22`, color: s.color }}
        >
          {s.label}
        </span>
        {isMine && <span className="text-[8px] font-mono text-[#FFD700] uppercase">{t("wc26.league.you")}</span>}
      </div>
    </div>
  );
}

/* ============================================================ */
/* SCHEDULE TAB — 33-day stage-grouped vertical timeline        */
/* ============================================================ */
function ScheduleTab({ pool, stages }) {
  const { t } = useT();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/wc26/schedule?pool=${pool}`).then((r) => setData(r.data)).catch(() => {});
  }, [pool]);
  if (!data) return <Loader2 className="w-5 h-5 animate-spin mx-auto mt-6 text-[#FFD700]" />;
  const today = new Date().toISOString().slice(0, 10);
  const fixtures = data.fixtures || [];
  const stageOrder = ["group", "r32", "r16", "qf", "sf", "final"];
  const stageMeta = {
    group: { label: t("wc26.league.stage.group"),   color: "#FFD700", stageKey: "Group Stage" },
    r32:   { label: t("wc26.league.stage.r32"),     color: "#10B981", stageKey: "Round of 32" },
    r16:   { label: t("wc26.league.stage.r16"),     color: "#3B82F6", stageKey: "Round of 16" },
    qf:    { label: t("wc26.league.stage.qf"),      color: "#8B5CF6", stageKey: "Quarterfinals" },
    sf:    { label: t("wc26.league.stage.sf"),      color: "#F97316", stageKey: "Semifinals" },
    final: { label: t("wc26.league.stage.final"),   color: "#EF4444", stageKey: "Final" },
  };
  const stagePcts = (stages || []).reduce((acc, s) => {
    const key = Object.keys(stageMeta).find((k) => stageMeta[k].stageKey === s.name);
    if (key) acc[key] = s.advance_pct;
    return acc;
  }, {});
  const grouped = stageOrder.map((id) => ({
    id, label: stageMeta[id].label, color: stageMeta[id].color,
    fixtures: fixtures.filter((f) => f.stage === id),
  })).filter((g) => g.fixtures.length > 0);
  return (
    <div className="space-y-4" data-testid="wc26-league-schedule">
      {grouped.map((stage, idx) => (
        <React.Fragment key={stage.id}>
          <section
            data-testid={`wc26-league-schedule-stage-${stage.id}`}
            className="rounded-2xl bg-[#0F172A] border overflow-hidden"
            style={{ borderColor: `${stage.color}33` }}
          >
            <div
              className="px-3 py-2 text-[10px] font-mono uppercase tracking-[0.3em]"
              style={{ background: `${stage.color}11`, color: stage.color, borderBottom: `1px solid ${stage.color}33` }}
            >
              // {stage.label} · {t("wc26.league.fixtureCount", { n: stage.fixtures.length })}
            </div>
            <div className="divide-y divide-white/5">
              {stage.fixtures.map((f) => {
                const isToday = f.date === today;
                const isPast = f.date < today;
                return (
                  <div
                    key={f.id}
                    className={`flex items-center justify-between px-3 py-2 text-[12px] ${isToday ? "bg-[#FFD700]/10" : ""}`}
                    data-testid={`wc26-league-schedule-fix-${f.id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-[10px] text-white/40 w-14 tabular-nums">D{f.day} {f.date.slice(5)}</span>
                      <span className="text-base">{flag(f.team_a)}</span>
                      <span className={`font-bold truncate ${isPast ? "text-white/50" : ""}`}>{f.team_a}</span>
                      <span className="text-white/30 mx-1">vs</span>
                      <span className={`font-bold truncate ${isPast ? "text-white/50" : ""}`}>{f.team_b}</span>
                      <span className="text-base">{flag(f.team_b)}</span>
                    </div>
                    {isToday && (
                      <span className="shrink-0 text-[8px] font-mono uppercase tracking-widest bg-[#FFD700] text-[#020617] rounded-full px-1.5 py-0.5 ml-2 animate-pulse">
                        {t("wc26.league.todayBadge")}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
          {idx < grouped.length - 1 && (
            <div
              className="rounded-xl text-center px-3 py-2 text-[10px] font-mono uppercase tracking-[0.3em] font-extrabold"
              style={{ background: "linear-gradient(90deg, transparent, #FFD70022 30%, #FFD70044 50%, #FFD70022 70%, transparent)", color: "#FFD700", border: "1px dashed #FFD70055" }}
              data-testid={`wc26-league-schedule-cut-${stage.id}`}
            >
              {t("wc26.league.cutAdvance", { pct: stagePcts[stage.id] ?? 30 })}
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

/* ============================================================ */
/* HISTORY TAB                                                  */
/* ============================================================ */
function HistoryTab({ pool, userId }) {
  const { t } = useT();
  const [matches, setMatches] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    api.get(`/wc26/history?pool=${pool}`).then((r) => setMatches(r.data.matches || [])).catch(() => {});
  }, [pool]);
  if (matches === null) return <Loader2 className="w-5 h-5 animate-spin mx-auto mt-6 text-[#FFD700]" />;
  if (matches.length === 0) return <div className="text-center text-sm text-white/60">{t("wc26.league.noMatches")}</div>;
  const tagFor = (m) => {
    if (m.status === "bye") return "bye";
    if (m.home_score === null || m.home_score === undefined) return "other";
    const iAmHome = m.home_user === userId;
    const draw = m.home_score === m.away_score;
    if (draw) return "draw";
    const won = iAmHome ? m.home_score > m.away_score : m.away_score > m.home_score;
    return won ? "win" : "loss";
  };
  const visible = matches.filter((m) => filter === "all" ? true : tagFor(m) === filter);
  return (
    <div className="space-y-2" data-testid="wc26-league-history">
      <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-1">
        {[
          { id: "all",  label: t("wc26.league.filter.all"),   color: "#FFD700" },
          { id: "win",  label: t("wc26.league.filter.win"),   color: "#10B981" },
          { id: "draw", label: t("wc26.league.filter.draw"),  color: "#FBBF24" },
          { id: "loss", label: t("wc26.league.filter.loss"),  color: "#EF4444" },
          { id: "bye",  label: t("wc26.league.filter.bye"),   color: "#3B82F6" },
        ].map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            data-testid={`wc26-league-history-filter-${f.id}`}
            className="shrink-0 px-2.5 py-1 rounded-full font-mono text-[10px] uppercase tracking-widest border transition-colors"
            style={filter === f.id ? { background: f.color, color: "#020617", borderColor: f.color } : { color: "#fff9", borderColor: "#ffffff1a" }}
          >{f.label}</button>
        ))}
      </div>
      {visible.length === 0 ? (
        <div className="text-center text-sm text-white/60 py-4">{t("wc26.league.noFilter")}</div>
      ) : visible.map((m) => {
        const iAmHome = m.home_user === userId;
        const myTeam = iAmHome ? m.team_a : m.team_b;
        const oppTeam = iAmHome ? m.team_b : m.team_a;
        const myPts = iAmHome ? m.points_home : m.points_away;
        const won = (iAmHome ? (m.home_score ?? 0) > (m.away_score ?? 0) : (m.away_score ?? 0) > (m.home_score ?? 0));
        const draw = (m.home_score === m.away_score) && m.home_score !== null;
        const result = m.status === "bye" ? "BYE" : (draw ? "D" : (won ? "W" : "L"));
        const color = m.status === "bye" ? "#10B981" : (draw ? "#FBBF24" : (won ? "#10B981" : "#EF4444"));
        return (
          <div key={m.id} className="flex items-center justify-between rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-[12px]">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-mono text-[10px] text-white/50">D{m.day}</span>
              <span className="text-base">{flag(myTeam)}</span>
              <span className="font-bold truncate">{myTeam}</span>
              <span className="text-white/30 mx-1">vs</span>
              <span className="text-base">{flag(oppTeam)}</span>
            </div>
            <div className="flex items-center gap-2">
              {m.home_score !== null && m.status !== "bye" && (
                <span className="font-mono tabular-nums text-[11px]">{iAmHome ? `${m.home_score}-${m.away_score}` : `${m.away_score}-${m.home_score}`}</span>
              )}
              <span className="font-mono text-[10px] uppercase font-extrabold" style={{ color }}>{result}</span>
              {myPts !== null && myPts !== undefined && (
                <span className="text-[#FFD700] font-extrabold text-[11px]">+{myPts}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
